import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getMediaUrl } from '../utils/mediaUrl';

/**
 * MentionSuggestions
 * Renders an autocomplete popover for @mentions in any textarea.
 * 
 * Props:
 * - textareaRef: RefObject pointing to the target HTMLTextAreaElement
 * - content: string (current text in the textarea)
 * - setContent: (newText: string) => void
 * - isVisible: boolean (optional control)
 */
const MentionSuggestions = ({ textareaRef, content, setContent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [mentionCursorIndex, setMentionCursorIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Check for active @mention at cursor
  const checkMention = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      setIsOpen(false);
      return;
    }

    const cursorPos = el.selectionStart;
    const textBeforeCursor = el.value.slice(0, cursorPos);

    // Look for @word immediately before cursor, preceded by start of string or whitespace
    const match = textBeforeCursor.match(/(?:^|\s)@([a-zA-Z0-9_]*)$/);

    if (match) {
      const q = match[1]; // e.g. "elhaf" or ""
      const atPos = textBeforeCursor.lastIndexOf('@');
      setQuery(q);
      setMentionStartIndex(atPos);
      setMentionCursorIndex(cursorPos);
      setIsOpen(true);
      setSelectedIndex(0);
    } else {
      setIsOpen(false);
      setSuggestions([]);
    }
  }, [textareaRef]);

  // Fetch mention suggestions (debounced)
  useEffect(() => {
    if (!isOpen) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/users/mentions/suggestions', {
          params: { q: query },
        });
        setSuggestions(res.data?.users || []);
      } catch (err) {
        console.warn('Mention suggestions fetch notice:', err.message);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 120);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [isOpen, query]);

  // Handle selecting a user
  const handleSelectUser = useCallback(
    (selectedUser) => {
      const el = textareaRef.current;
      if (!selectedUser || !el || mentionStartIndex === -1) return;

      const handle = (selectedUser.handle || '').replace(/^@+/, '');
      const mentionText = `@${handle} `;

      const before = content.slice(0, mentionStartIndex);
      const after = content.slice(mentionCursorIndex);
      const newContent = before + mentionText + after;

      setContent(newContent);
      setIsOpen(false);
      setSuggestions([]);

      // Move cursor after the inserted mention
      const newPos = before.length + mentionText.length;
      setTimeout(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(newPos, newPos);
        }
      }, 20);
    },
    [content, mentionCursorIndex, mentionStartIndex, setContent, textareaRef]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || suggestions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleSelectUser(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [handleSelectUser, isOpen, selectedIndex, suggestions]
  );

  // Attach keydown & input listeners to target textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const onInput = () => checkMention();
    const onClick = () => checkMention();
    const onKeyUp = (e) => {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
        checkMention();
      }
    };

    el.addEventListener('input', onInput);
    el.addEventListener('click', onClick);
    el.addEventListener('keyup', onKeyUp);
    el.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('input', onInput);
      el.removeEventListener('click', onClick);
      el.removeEventListener('keyup', onKeyUp);
      el.removeEventListener('keydown', handleKeyDown);
    };
  }, [checkMention, handleKeyDown, textareaRef]);

  // Click outside listener
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isOpen, textareaRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="absolute z-50 mt-1 w-64 sm:w-72 max-h-56 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150"
      style={{
        top: '100%',
        left: 0,
      }}
    >
      <div className="px-3 py-1.5 border-b border-zinc-100 dark:border-zinc-800/80 text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider flex items-center justify-between">
        <span>Mention someone</span>
        {loading && <i className="fas fa-spinner fa-spin text-sky-500" />}
      </div>

      <div className="overflow-y-auto max-h-48 py-1 divide-y divide-zinc-50 dark:divide-zinc-800/40">
        {loading && suggestions.length === 0 ? (
          <div className="p-3 text-center text-xs text-zinc-400 dark:text-zinc-500">
            Searching users...
          </div>
        ) : suggestions.length === 0 ? (
          <div className="p-3 text-center text-xs text-zinc-400 dark:text-zinc-500">
            No matching users found
          </div>
        ) : (
          suggestions.map((u, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // Don't steal focus from textarea
                  handleSelectUser(u);
                }}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  isSelected
                    ? 'bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400'
                    : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/60 text-zinc-900 dark:text-zinc-100'
                }`}
              >
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {u.profilePicture ? (
                    <img
                      src={getMediaUrl(u.profilePicture)}
                      alt={u.username}
                      className="w-8 h-8 rounded-full object-cover border border-zinc-200 dark:border-zinc-800"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 flex items-center justify-center font-bold text-xs">
                      {(u.username || u.handle || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className="font-bold text-xs sm:text-sm text-zinc-900 dark:text-zinc-100 truncate">
                      {u.username}
                    </span>
                    {u.isVerified && (
                      <i className="fas fa-check-circle text-sky-500 text-[10px] flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 dark:text-zinc-500 truncate">
                    @{u.handle}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MentionSuggestions;
